import { Divider, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useEffect, useState } from 'react';

import { ROUTES } from 'enrollment/app/routes';
import { InternalPage, Loader } from 'theSource/components';
import { AddYourBeneficiariesPaperStyled } from './AddYourBeneficiariesStyled';
import { useEnrollmentBeneficiaryQuery } from 'enrollment/queries/useEnrollmentAddBeneficiaryQuery';
import { Beneficiaries } from './components/Beneficiaries';
import { filterBeneficiaries } from 'enrollment/utils/common/Utils';
import { PsButtonTuner } from 'stencilSetup';
import { setBeneficiarySelections } from './slice';
import { BackLink, PageContentHeaderDisplay } from 'enrollment/components';
import {
  convertCurrency,
  getPremiumStyled,
  RETURN_VALUE_ONLY,
} from 'enrollment/utils';
import {
  MaxSliderValueContainer,
  StackedLineInfo,
} from '../Plans/PlanCard/StackedVolume';
import { CoverageLineDto } from 'enrollment/queries/useBenefitPlansQuery/types';
import { useBenefitCategoryQuery } from 'enrollment/queries';
import { queryKeyGenerator, queryClient } from 'theSource/utils';
import { setAllBeneficiaryData } from './services';
import {
  selectEnrollmentContext,
  selectEnrollmentOrgPlanId,
  selectEnrollmentOrgPlanYearId,
} from 'theSource/features/Home/components/EnrollmentBanner/services';
import { useConfigurationQuery } from 'theSource/queries';
import { filteredBeneficiaries } from '../Plans/PlanCard/utils';
import { ThemedHeader } from 'components/ThemedHeader';

export const AddYourBeneficiaries = () => {
  const [disable, setDisabled] = useState(true);
  const { t } = useTranslation('pages.enrollmentAddBeneficiaries');
  const theme = useTheme();
  const desktopViewConditions = useMediaQuery(theme.breakpoints.up('sm'));
  const orgPlanYearId = useSelector(selectEnrollmentOrgPlanId);
  const enrollmentContext = useSelector(selectEnrollmentContext);
  let orgPlanYearContextId = useSelector(selectEnrollmentOrgPlanYearId);
  orgPlanYearContextId =
    enrollmentContext === 'life_event' ? orgPlanYearContextId : 0;
  const benefitPlansState = useSelector(
    (state: any) => state?.enrollment?.beneficiary?.beneficiaryData
  );
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { configuration } = useConfigurationQuery();
  const lumosApiHost = configuration?.apiHost;
  const queryKey = queryKeyGenerator(
    `benefits-details-${enrollmentContext}`,
    Number(orgPlanYearContextId)
  );
  const { data: benefitData, isLoading: isBenefitsLoading } =
    useBenefitCategoryQuery({
      key: queryKey,
      enrollmentContextType: enrollmentContext,
      enabled: true,
      lumosApiHost: String(lumosApiHost),
      includeEligibleDependentsData: true,
      orgPlanYearId: orgPlanYearContextId,
    });

  useEffect(() => {
    if (benefitData && !isBenefitsLoading) {
      const initialPlansState = filteredBeneficiaries(benefitData)?.filter(
        (benficiaryData) =>
          benficiaryData !== null && benficiaryData !== undefined
      );
      initialPlansState?.length &&
        dispatch(setAllBeneficiaryData(initialPlansState));
    }
  }, [benefitData, isBenefitsLoading, dispatch]);

  const { data: beneficiaryDetails, isLoading: isBeneficiaryLoading } =
    useEnrollmentBeneficiaryQuery({
      selectionOptions: true,
    });

  useEffect(() => {
    if (beneficiaryDetails) {
      dispatch(
        setBeneficiarySelections(
          beneficiaryDetails?.data?.beneficiary_selection_options
        )
      );
    }
  }, [beneficiaryDetails, dispatch]);

  const metadata = beneficiaryDetails?.data?.metadata;
  const allBeneficiaries = useSelector(
    (state: any) =>
      state?.enrollment?.storeBeneficiarySelection?.beneficiarySelectionOptions
  );
  const emptyPrimaryBeneficiaries = benefitPlansState
    ?.map((benefitPlan: any) => {
      return (
        filterBeneficiaries(benefitPlan?.beneficiaries, true)?.length === 0
      );
    })
    ?.includes(true);

  if (isBeneficiaryLoading) {
    return <Loader />;
  }

  const handleSubmit = () => {
    queryClient.invalidateQueries(queryKey);
    navigate(`/enrollment/${ROUTES.enrollment.benefitDetails.path}`);
  };

  return (
    <div className="pt-5 pb-4 pt-2_5 px-4 pt-md-5 px-md-0 mt-md-5 mt-lg-0">
      <BackLink className="mt-md-4 mt-lg-0" />
      <InternalPage testId="enrollment-family-details-page">
        <ThemedHeader cssClass="mt-5">{t('title')}</ThemedHeader>
        <PageContentHeaderDisplay
          pageContentLookupCode="enroll_beneficiary"
          displayPageHeader={false}
          orgPlanYearId={orgPlanYearId}
        />
        {/* --- Card Start ---*/}
        <>
          {benefitPlansState?.map((planDetails: any) => {
            const stackedCov = planDetails?.stackedCoverageLines?.length > 0;
            return (
              <AddYourBeneficiariesPaperStyled
                key={planDetails?.orgBenefitId}
                id={`coverage-plan-${planDetails?.orgBenefitId}`}
              >
                <div className="d-flex flex-column-reverse flex-md-row align-items-center px-4 py-3">
                  <div className="text-center text-md-left flex-md-fill">
                    <h2 className="headline-3 mt-2">
                      {planDetails?.orgBenefitName}
                    </h2>
                    <p className="body2">
                      {t('planCoverageAmount')}:{' '}
                      <span className="semiBold">
                        {getPremiumStyled({
                          premium: planDetails?.coverageFields?.req_volume,
                          valueOnly: RETURN_VALUE_ONLY,
                        })}
                      </span>
                    </p>
                  </div>
                  <div className="text-center text-md-right">
                    {planDetails?.coverageFields?.carrier_logo_url ? (
                      <img
                        src={planDetails?.coverageFields?.carrier_logo_url}
                        alt="Carrier Logo"
                        data-testid="logo"
                      />
                    ) : null}

                    {!planDetails?.coverageFields?.carrier_logo_url && (
                      <Typography variant="body2" sx={{ width: '20rem' }}>
                        <span className="semiBold">
                          {planDetails?.coverageFields?.carrier_name}
                        </span>
                      </Typography>
                    )}
                  </div>
                </div>
                {stackedCov ? (
                  <>
                    {planDetails?.stackedCoverageLines?.map(
                      (line: CoverageLineDto, index: number) => {
                        return (
                          <>
                            <MaxSliderValueContainer className="pl-4_5 pt-3 border-top">
                              <StackedLineInfo>
                                <h3>
                                  {t('policyLine')} {index + 1}
                                </h3>
                                <p className="body2">
                                  {t('planCoverageAmount')}:{' '}
                                  <strong>
                                    {convertCurrency(line.req_volume)}
                                  </strong>
                                </p>
                              </StackedLineInfo>
                              <p className="pr-4_5 d-flex align-items-end">
                                {t('effDate')}: {line.change_effective_date}
                              </p>
                            </MaxSliderValueContainer>
                            <Beneficiaries
                              key={`primary-beneficiary-${planDetails?.orgBenefitId}-${index}`}
                              orgBenefitId={planDetails?.orgBenefitId}
                              isPrimary={true}
                              setDisabled={setDisabled}
                              emptyPrimaryBeneficiaries={
                                emptyPrimaryBeneficiaries
                              }
                              coverageFields={planDetails?.coverageFields}
                              allBeneficiaries={allBeneficiaries}
                              metadata={metadata}
                              coverageLineId={line.id ? line.id : 0}
                              numCoverageLines={
                                planDetails?.stackedCoverageLines?.length
                              }
                            />
                            {desktopViewConditions && (
                              <Divider
                                orientation="horizontal"
                                variant="middle"
                                aria-hidden="true"
                                role="presentation"
                              />
                            )}
                            <Beneficiaries
                              key={`secondary-beneficiary-${planDetails?.orgBenefitId}-${index}`}
                              orgBenefitId={planDetails?.orgBenefitId}
                              isPrimary={false}
                              setDisabled={setDisabled}
                              emptyPrimaryBeneficiaries={
                                emptyPrimaryBeneficiaries
                              }
                              coverageFields={planDetails?.coverageFields}
                              allBeneficiaries={allBeneficiaries}
                              metadata={metadata}
                              coverageLineId={line.id ? line.id : 0}
                              numCoverageLines={
                                planDetails?.stackedCoverageLines?.length
                              }
                            />
                          </>
                        );
                      }
                    )}
                  </>
                ) : (
                  <div className="px-4 pb-1">
                    <Beneficiaries
                      key={`primary-beneficiary-${planDetails?.orgBenefitId}`}
                      orgBenefitId={planDetails?.orgBenefitId}
                      isPrimary={true}
                      setDisabled={setDisabled}
                      emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
                      coverageFields={planDetails?.coverageFields}
                      allBeneficiaries={allBeneficiaries}
                      metadata={metadata}
                    />
                    {desktopViewConditions && <hr className="mb-4_5" />}
                    <Beneficiaries
                      key={`secondary-beneficiary-${planDetails?.orgBenefitId}`}
                      orgBenefitId={planDetails?.orgBenefitId}
                      isPrimary={false}
                      setDisabled={setDisabled}
                      emptyPrimaryBeneficiaries={emptyPrimaryBeneficiaries}
                      coverageFields={planDetails?.coverageFields}
                      allBeneficiaries={allBeneficiaries}
                      metadata={metadata}
                    />
                  </div>
                )}
              </AddYourBeneficiariesPaperStyled>
            );
          })}
        </>
        <div className="text-center text-md-left">
          <PsButtonTuner
            buttonId="continue-button"
            label={t('continue')}
            level="primary"
            disabled={disable ? true : false}
            onClick={handleSubmit}
          />
        </div>
      </InternalPage>
    </div>
  );
};
